import React from 'react';
import styled, { ThemeContext } from 'styled-components';
import { Img } from 'react-image';
import Plx from 'react-plx';
import {
  BigButton, PaddedContainer, SectionName, SectionTitle, buildPlxData,
} from '../common/styles';
import dt, { useIsXLarge } from '../../../shared/theme';

const vPadding = 140;
const Container = styled(PaddedContainer)`
  padding: ${vPadding + 8}px 0;
  display: flex;
  ${props => (dt(props).theme.isXLarge ? `
    align-items: start;
    justify-content: center;
  ` : `
    flex-direction: column;
    align-items: center;
  `)};
`;

const r = 530 / 443;
const wDesktop = 528;
const wMobile = 90;
const Image = styled(Img)`
  ${props => (props.theme.isMobile ? `
    width: ${wMobile}vw;
    height: ${wMobile / r}vw;
  ` : `
    width: ${wDesktop}px;
    height: ${wDesktop / r}px;
  `)};
`;

// The reason we need a different sized pseudo :before shadow other than the
// original image size, is the image irregular on shadow shape.
const shadowH = 400;
const ImageContainer = styled.div`
  ${props => (props.theme.isMobile ? `
    order: 2;
    width: ${wMobile}vw;
    height: ${wMobile / r}vw;
  ` : `
    width: ${wDesktop}px;
    height: ${wDesktop / r}px;
    margin-right: 59px;
  `)};

  &:before {
    z-index: 100;
    content: "";
    display: block;
    position: relative;
    box-shadow: 0 3px 20px ${props => props.theme.secondaryColor30Transparent};
    ${props => (props.theme.isMobile ? `
      width: ${wMobile}vw;
      height: ${shadowH * wMobile / wDesktop}vw;
    ` : `
      width: ${wDesktop}px;
      height: ${shadowH}px;
    `)};
  }

  & > ${Image} {
    position: relative;
    top: -${props => (props.theme.isMobile ? `${shadowH * wMobile / wDesktop}vw` : `${shadowH}px`)};
    z-index: 150;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  ${props => (dt(props).theme.isXLarge ? `
    width: 31.25vw;
    margin-left: 59px;
    max-width: 450px;
  ` : `
    width: 100%;
    margin-bottom: 40px;
    max-width: unset;
  `)};
`;

const Name = styled(SectionName)`
  margin-bottom: 20px;
`;

const Title = styled(SectionTitle)`
  margin-top: 0;
  margin-bottom: 20px;
  ${props => (props.theme.isMobile && `
    font-size: 30px;
    line-height: 36px;
  `)};
`;

const Desc = styled.p`
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: ${props => props.theme.fontWeightMedium};
  font-size: 20px;
  line-height: 25px;
  margin-top: 0;
  margin-bottom: 32px;
`;

const Button = styled(BigButton)`
  width: 300px;
`;

function redirectToFeatures(e) {
  if (e) {
    e.preventDefault();
  }
  window.location = '/company/features';
}

export default function Features() {
  const { feature } = React.useContext(ThemeContext);
  const plxData = buildPlxData('#feature-container', vPadding);

  const isXLarge = useIsXLarge();

  return (
    <Container id="feature-container">
      <Plx parallaxData={plxData.image} disabled={!isXLarge}>
        <ImageContainer>
          <Image src={feature} />
        </ImageContainer>
      </Plx>
      <Plx parallaxData={plxData.text} disabled={!isXLarge}>
        <TextContainer>
          <Name>features</Name>
          <Title>Tools to help discover, evaluate and monitor your next investment.</Title>
          <Desc>
            We seek to simplify the complexities of making investment decisions.
            Our tools are designed to support your process and ensure you are fully
            informed on key financial markets.
          </Desc>
          <Button onClick={redirectToFeatures}>discover more features</Button>
        </TextContainer>
      </Plx>
    </Container>
  );
}
